import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CountryFlag from '@/components/CountryFlag';
import IcChevronDown from '@/assets/icons/ChevronDown.svg?react';
import IcMagnifyingGlass from '@/assets/icons/MagnifyingGlass.svg?react';
import IcCross from '@/assets/icons/Cross.svg?react';
import IcCheck from '@/assets/icons/Check.svg?react';
import computeFlyoutPos, {
  type FlyoutPos,
} from '@/utils/computeFlyoutPos.util';
import type { Country } from '../types';
import styles from './CountrySelect.module.scss';

/**
 * Generous estimate so `computeFlyoutPos` is more willing to flip the dropdown
 * upward when the trigger sits low on the page. An under-estimate makes the
 * util pick "downward" and the dropdown ends up cramped at the bottom edge.
 */
const ESTIMATED_FLYOUT_HEIGHT = 480;

/**
 * Extra viewport clearance for the picker so the dropdown doesn't kiss the
 * top or bottom of the window.
 */
const VIEWPORT_SAFETY_MARGIN = 24;

/**
 * Normalises a search entry to a dial-prefix query: "+43", "0043" and bare
 * "43" all become "+43". Returns null when the entry isn't prefix-shaped
 * (not just digits with an optional "+"/"00"), so only name matching applies.
 */
const toPrefixQuery = (query: string): string | null => {
  const compact = query.replace(/[\s\-()]/g, '').replace(/^00/, '+');
  const normalised = compact.startsWith('+') ? compact : `+${compact}`;
  return /^\+\d+$/.test(normalised) ? normalised : null;
};

interface CountrySelectProps {
  countries: Country[];
  value: string;
  onChange: (iso: string) => void;
  isTouch?: boolean;
}

const CountrySelect = ({
  countries,
  value,
  onChange,
  isTouch = false,
}: CountrySelectProps) => {
  const { t } = useTranslation(['common']);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  // Placeholder until `open()` runs; the dialog isn't mounted until then.
  const [dialogPos, setDialogPos] = useState<FlyoutPos>({
    left: 0,
    width: 0,
    maxHeight: 0,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogNodeRef = useRef<HTMLDialogElement | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const describedById = useId();
  const titleId = useId();

  const selectedCountry =
    countries.find((c) => c.iso === value) ?? countries[0];

  const query = searchText.trim().toLowerCase();
  const prefixQuery = toPrefixQuery(query);
  const filteredCountries = searchText
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          (prefixQuery !== null && c.prefix.startsWith(prefixQuery))
      )
    : countries;

  const close = useCallback((restoreFocus = true) => {
    setIsVisible(false);
    setIsOpen(false);
    setSearchText('');
    if (restoreFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  const dialogRef = useCallback(
    (dialog: HTMLDialogElement | null) => {
      dialogNodeRef.current = dialog;
      if (!dialog) {
        return;
      }
      dialog.showModal();
      // Focus search after the modal is open. On touch we delay slightly so the
      // slide-up animation isn't interrupted by the keyboard appearing.
      if (isTouch) {
        window.setTimeout(() => searchRef.current?.focus(), 220);
      } else {
        searchRef.current?.focus();
      }

      // Trigger the entry animation on the next frame.
      requestAnimationFrame(() => setIsVisible(true));

      const handleClick = (e: MouseEvent) => {
        if (e.target === dialog) {
          close();
        }
      };
      const handleCancel = (e: Event) => {
        e.preventDefault();
        close();
      };

      dialog.addEventListener('click', handleClick);
      dialog.addEventListener('cancel', handleCancel);
    },
    [close, isTouch]
  );

  const open = () => {
    if (!triggerRef.current) {
      return;
    }
    setSearchText('');
    setIsOpen(true);

    if (isTouch) {
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const options = { safetyMargin: VIEWPORT_SAFETY_MARGIN };
    setDialogPos(computeFlyoutPos(rect, ESTIMATED_FLYOUT_HEIGHT, options));
    // Second pass: once the dialog has its real height, recompute to use it.
    requestAnimationFrame(() => {
      const dialog = dialogNodeRef.current;
      if (!dialog) {
        return;
      }
      const actualHeight =
        dialog.getBoundingClientRect().height || ESTIMATED_FLYOUT_HEIGHT;
      setDialogPos(computeFlyoutPos(rect, actualHeight, options));
    });
  };

  // Lock background scroll while the sheet is open on touch devices.
  useEffect(() => {
    if (!isOpen || !isTouch) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen, isTouch]);

  const handleSelect = (iso: string) => {
    // Close without restoring focus to the trigger — the parent will route
    // focus to the phone number input instead.
    close(false);
    onChange(iso);
  };

  return (
    <div className={styles.wrapper}>
      <p className={styles.visuallyHidden} id={describedById}>
        {t('common:country')}
      </p>

      <button
        ref={triggerRef}
        type="button"
        className={classNames(styles.trigger, { [styles.triggerOpen]: isOpen })}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-describedby={describedById}
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) {
            close();
          } else {
            open();
          }
        }}
      >
        <CountryFlag iso={value} className={styles.flag} />
        <span className={styles.prefix}>{selectedCountry?.prefix}</span>
        <IcChevronDown className={styles.caret} aria-hidden="true" />
      </button>

      {isOpen && (
        <dialog
          ref={dialogRef}
          className={classNames(styles.flyout, {
            [styles.flyoutTouch]: isTouch,
            [styles.flyoutVisible]: isVisible,
          })}
          aria-labelledby={titleId}
          style={
            isTouch
              ? undefined
              : {
                  top:
                    dialogPos.top !== undefined ? `${dialogPos.top}px` : 'auto',
                  bottom:
                    dialogPos.bottom !== undefined
                      ? `${dialogPos.bottom}px`
                      : 'auto',
                  left: `${dialogPos.left}px`,
                  maxHeight:
                    dialogPos.maxHeight !== undefined
                      ? `${dialogPos.maxHeight}px`
                      : undefined,
                }
          }
        >
          {isTouch && <span className={styles.grab} aria-hidden="true" />}

          <div className={styles.header}>
            <h2 id={titleId} className={styles.title}>
              {t('common:country')}
            </h2>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => close()}
              aria-label={t('common:button.cancel')}
            >
              <IcCross />
            </button>
          </div>

          <div className={styles.searchRow}>
            <IcMagnifyingGlass
              className={styles.searchIcon}
              aria-hidden="true"
            />
            <input
              ref={searchRef}
              type="search"
              className={styles.searchInput}
              placeholder={t('common:search.placeholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label={t('common:search.placeholder')}
            />
          </div>

          {filteredCountries.length === 0 ? (
            <div className={styles.empty} role="status">
              {t('common:search.noResults')}
            </div>
          ) : (
            <ul
              className={styles.list}
              role="listbox"
              aria-label={t('common:country')}
            >
              {filteredCountries.map((country, index) => {
                const prevCountry = filteredCountries[index - 1];
                const showDivider =
                  !searchText &&
                  index > 0 &&
                  country.rank === 0 &&
                  (prevCountry?.rank ?? 0) > 0;
                const isSelected = country.iso === value;
                return (
                  <li
                    key={country.iso}
                    role="option"
                    aria-selected={isSelected}
                    className={classNames(styles.option, {
                      [styles.optionSelected]: isSelected,
                      [styles.optionWithDivider]: showDivider,
                    })}
                    onClick={() => handleSelect(country.iso)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(country.iso);
                      }
                    }}
                    tabIndex={0}
                  >
                    <CountryFlag
                      iso={country.iso}
                      className={styles.optionFlag}
                    />
                    <span className={styles.countryName}>{country.name}</span>
                    <span className={styles.countryPrefix}>
                      {country.prefix}
                    </span>
                    {isSelected && (
                      <IcCheck
                        className={styles.checkIcon}
                        aria-hidden="true"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </dialog>
      )}
    </div>
  );
};

export default CountrySelect;
